import { ROUTES } from "@/routes/Routes";
import { requestGetBalanceForOffice } from "@/state/balance/BalanceEffects";
import { Facturation } from "@/type/core/offices/offices.types";
import SherlockHelper from "@/utils/helpers/sherlock-helper";
import { toastError } from "@/utils/helpers/toast-helper";
import BasicButton from "@components/core/buttons/BasicButton";
import InfoModal from "@components/core/modals/InfoModal";
import { ActiveUser } from "@type/core/auth/auth.types";
import { TransactionDetail } from "@type/core/transaction/transaction.types";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import OrderModal from "@components/core/modals/OrderModal";

interface CreditConditionalButtonProps {
  user?: ActiveUser;
  transaction: TransactionDetail;
  variant?: string;
  label: string;
  disabled: boolean;
  onClick: () => void;
  isRelaunch?: boolean;
  isDirectSignature?: boolean;
  className?: string;
}

const CreditConditionalButton: FunctionComponent<
  CreditConditionalButtonProps
> = ({
  user,
  transaction,
  variant = "primary",
  label,
  disabled,
  onClick,
  isRelaunch = false,
  isDirectSignature = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const creditExhausted = transaction.potentialCreditExhausted;
  const isInGroup = transaction.isInGroup;
  const office = useMemo(
    () =>
      user &&
      user.officesForCgu &&
      user.officesForCgu.find((o) => transaction.officeId === o.id),
    [user?.officesForCgu],
  );

  const [isOrderModalVisible, setIsOrderModalVisible] =
    useState<boolean>(false);
  const [facturation, setFacturation] = useState<Facturation | undefined>();

  useEffect(() => {
    if (office) {
      void requestGetBalanceForOffice({
        dto: {
          officeIds: office.id,
        },
      });
    }
  }, [office]);

  useEffect(() => {
    void requestGetBalanceForOffice.done.watch(({ result }) => {
      if (result && result.ok) {
        setFacturation(result.data);
      } else {
        toastError(t("error.toast.officesBalance"));
      }
    });
  }, []);

  const handleLinkClickInsufficientCreditAddress = () => {
    if (office) {
      navigate(
        ROUTES.extranet.menu.offices.officeWithBillingAddress.generate(
          office.id,
        ),
      );
    }
  };

  const handleLinkClickInsufficientCreditCollaborator = () =>
    office?.billing
      ? setIsOrderModalVisible(true)
      : navigate(
          ROUTES.extranet.menu.offices.balanceAndStatistics.generate(
            transaction.officeId,
          ),
        );

  const orderModal = () => {
    return office?.billing ? (
      <OrderModal
        title={t("modals.orderModal.title")}
        visible={isOrderModalVisible}
        onCancel={() => setIsOrderModalVisible(false)}
        officeBalance={facturation?.officesBalance.find(
          (facture) => facture.officeId === office?.id,
        )}
        billing={office.billing}
        onSubmit={(tarif) => {
          location.href = SherlockHelper.buildOrderUrl(office.id, tarif);
        }}
      />
    ) : null;
  };

  if (isInGroup || !creditExhausted || isRelaunch || isDirectSignature) {
    return (
      <div className="d-flex align-items-center gap-4">
        <BasicButton
          variant={variant}
          disabled={disabled}
          text={label}
          onClick={onClick}
          className={className}
        />
      </div>
    );
  }
  if (office && office.expertComptable) {
    if (office.billing && office.billing.address1 && office.billing.zipCode) {
      return (
        <div className="d-flex align-items-center gap-4">
          <BasicButton
            variant={variant}
            disabled={true}
            text={label}
            onClick={onClick}
            className={className}
          />
          <InfoModal
            value="insufficientCredit"
            warning
            linkText={t("modals.info.insufficientCredit.linkText")}
            linkOnClick={() => setIsOrderModalVisible(true)}
            info={false}
          />
          {orderModal()}
        </div>
      );
    }

    return (
      <div className="d-flex align-items-center gap-4">
        <BasicButton
          variant={variant}
          disabled={true}
          text={label}
          onClick={onClick}
          className={className}
        />
        <InfoModal
          value="insufficientCreditAddress"
          warning
          linkOnClick={handleLinkClickInsufficientCreditAddress}
          linkText={t("modals.info.insufficientCreditAddress.linkText")}
          info={false}
        />
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center gap-4">
      <BasicButton
        variant={variant}
        disabled={!isInGroup}
        text={label}
        onClick={onClick}
        className={className}
      />
      {!isInGroup && (
        <InfoModal
          value="insufficientCreditCollaborator"
          warning
          linkText={t("modals.info.insufficientCreditCollaborator.linkText")}
          linkOnClick={handleLinkClickInsufficientCreditCollaborator}
          info={false}
        />
      )}
      {orderModal()}
    </div>
  );
};

export default CreditConditionalButton;
